import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Gift, ArrowRight, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  sharer_name: "",
  sharer_email: "",
  sharer_phone: "",
  friend_name: "",
  friend_phone: "",
  friend_email: "",
  note: "",
};

export default function Referral() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.sharer_name || !form.sharer_email || !form.friend_name) {
      toast.error("Please add your name, your email, and your friend's name.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/referrals`, {
        sharer_name: form.sharer_name,
        sharer_email: form.sharer_email,
        sharer_phone: form.sharer_phone || null,
        friend_name: form.friend_name,
        friend_phone: form.friend_phone || null,
        friend_email: form.friend_email || null,
        note: form.note || null,
      });
      toast.success("Referral submitted — we'll be in touch!");
      setDone(true);
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit referral.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="refer"
      data-testid="referral-section"
      className="relative py-24 lg:py-28 bg-black"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-sm border border-gold/30 bg-gradient-to-br from-[#0c0a05] via-[#0a0a0a] to-[#0c0a05] p-8 lg:p-14 reveal">
          <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-gold/5 blur-3xl" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Left — pitch + how it works */}
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-8 bg-gold" />
                <span className="text-xs uppercase tracking-[0.32em] text-gold/90">
                  Refer a Friend
                </span>
              </div>
              <h2
                data-testid="referral-title"
                className="font-display font-light text-4xl lg:text-5xl tracking-tight leading-[1.04]"
              >
                Send a friend our way —
                <br />
                <span className="italic gold-shimmer">
                  you get $20 off your next detail.
                </span>
              </h2>
              <p className="mt-6 text-white/65 max-w-lg leading-relaxed">
                Already a happy customer? Tell us who you&apos;d like to
                refer. When they complete their first detail with us,
                we&apos;ll apply <span className="text-white">$20 off</span>{" "}
                to your next appointment.
              </p>

              <ol className="mt-8 space-y-4">
                {[
                  "Fill out the form with your friend's info",
                  "We reach out to them with a friendly intro",
                  "When they finish their first detail, your $20 credit kicks in",
                ].map((step, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-white/75 text-sm"
                  >
                    <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full border border-gold/50 text-gold text-xs font-medium shrink-0">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Right — form OR success */}
            <div className="lg:col-span-7">
              {done ? (
                <div
                  data-testid="referral-success"
                  className="bg-black/60 border border-gold/40 rounded-sm p-10 lg:p-14 text-center backdrop-blur-md"
                >
                  <div className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-gold text-gold mb-6">
                    <Sparkles size={22} />
                  </div>
                  <h3 className="font-display text-3xl lg:text-4xl">
                    Thanks for the referral!
                  </h3>
                  <p className="mt-4 text-white/65 max-w-md mx-auto">
                    We&apos;ll reach out to your friend soon. Your $20 credit
                    will be ready to use after their first detail.
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    data-testid="referral-again"
                    className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold hover:text-[#F3E5AB]"
                  >
                    Refer another friend
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="referral-form"
                  className="bg-black/60 border border-white/10 rounded-sm p-7 lg:p-10 backdrop-blur-md relative"
                >
                  <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

                  <div className="flex items-center gap-3 mb-7">
                    <div className="h-10 w-10 grid place-items-center rounded-full border border-gold/40 text-gold">
                      <Gift size={16} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">
                        Quick form
                      </div>
                      <div className="font-display text-lg">Your referral</div>
                    </div>
                  </div>

                  {/* You */}
                  <div className="text-[10px] uppercase tracking-[0.28em] text-gold/80 mb-3">
                    About you
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field
                      label="Your first and last name"
                      name="sharer_name"
                      testid="referral-input-sharer-name"
                      value={form.sharer_name}
                      onChange={onChange}
                      required
                      autoComplete="name"
                    />
                    <Field
                      label="Your email"
                      name="sharer_email"
                      type="email"
                      testid="referral-input-sharer-email"
                      value={form.sharer_email}
                      onChange={onChange}
                      required
                      autoComplete="email"
                    />
                    <div className="sm:col-span-2">
                      <Field
                        label="Your phone (optional)"
                        name="sharer_phone"
                        testid="referral-input-sharer-phone"
                        value={form.sharer_phone}
                        onChange={onChange}
                        placeholder="So we can text you when the credit is ready"
                        autoComplete="tel"
                      />
                    </div>
                  </div>

                  {/* Friend */}
                  <div className="text-[10px] uppercase tracking-[0.28em] text-gold/80 mb-3 mt-8">
                    About your friend
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-2">
                      <Field
                        label="Friend's first and last name"
                        name="friend_name"
                        testid="referral-input-friend-name"
                        value={form.friend_name}
                        onChange={onChange}
                        required
                      />
                    </div>
                    <Field
                      label="Friend's phone (optional)"
                      name="friend_phone"
                      testid="referral-input-friend-phone"
                      value={form.friend_phone}
                      onChange={onChange}
                      placeholder="e.g. 206-555-1234"
                    />
                    <Field
                      label="Friend's email (optional)"
                      name="friend_email"
                      type="email"
                      testid="referral-input-friend-email"
                      value={form.friend_email}
                      onChange={onChange}
                      placeholder="e.g. friend@example.com"
                    />
                  </div>

                  <div className="mt-6">
                    <Label
                      htmlFor="note"
                      className="text-xs uppercase tracking-[0.22em] text-white/60"
                    >
                      Anything we should know? (optional)
                    </Label>
                    <Textarea
                      id="note"
                      name="note"
                      data-testid="referral-input-note"
                      value={form.note}
                      onChange={onChange}
                      rows={3}
                      autoComplete="off"
                      className="gold-input mt-3 bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-0"
                      placeholder="e.g. 'They've got a black 2020 SUV that really needs an interior detail'"
                    />
                  </div>

                  <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <p className="text-xs text-white/40 max-w-sm">
                      Your $20 credit applies after your friend completes
                      their first detail with us.
                    </p>
                    <Button
                      type="submit"
                      data-testid="referral-submit"
                      disabled={submitting}
                      className="bg-gold text-black hover:bg-[#F3E5AB] rounded-full px-7 py-6 uppercase tracking-[0.22em] text-xs font-medium gap-2"
                    >
                      {submitting ? "Sending..." : "Send referral"}
                      <ArrowRight size={14} />
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", testid, value, onChange, required, placeholder, autoComplete }) {
  return (
    <div>
      <Label
        htmlFor={name}
        className="text-xs uppercase tracking-[0.22em] text-white/60"
      >
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </Label>
      <Input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete || "off"}
        data-testid={testid}
        className="gold-input mt-3 bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-0 h-12"
      />
    </div>
  );
}
